/**
 * Compact trigger + searchable option list (portaled).
 * Closed state can show a short display label; open menu shows full labels and filters by substring.
 */
import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import {
  isSameAnchoredMenuPos,
  placeAnchoredMenu,
  type AnchoredMenuPos,
} from '../../utils/ui/placeAnchoredMenu';
import { partitionCountrySelectOptions } from '../../utils/countrySelectGrouping';

export type SearchableSelectOption = {
  value: string;
  /** Full label shown in the open menu. */
  label: string;
  /** Optional short label for the closed trigger (e.g. ISO country code). */
  displayLabel?: string;
  /** Extra text included in search matching (e.g. ISO code). */
  searchText?: string;
  disabled?: boolean;
};

export type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  title?: string;
  'aria-label'?: string;
  /** Compact = tight width for abbreviations; default = medium for names. */
  size?: 'compact' | 'default';
  /** Dense reduces the closed trigger height for data-table rows. */
  dense?: boolean;
  /** Fill the parent cell so opening the menu does not change column width. */
  fill?: boolean;
  className?: string;
  /** When true, open the menu on mount (inline-edit flows). */
  autoOpen?: boolean;
  /** Called when the menu closes without a selection change (optional). */
  onClose?: () => void;
  clearLabel?: string;
  allowClear?: boolean;
  /** Country cells: name left, ISO-2 right, check when selected. Other menus stay label-only. */
  variant?: 'default' | 'country';
  /**
   * Country variant only: values already on this list, shown above a divider.
   * Omit or pass [] to keep parent option order (do not treat a full world list as in-use).
   */
  priorityValues?: readonly string[];
};

function matchesQuery(option: SearchableSelectOption, query: string): boolean {
  if (!query) return true;
  const haystack = `${option.label} ${option.displayLabel || ''} ${option.searchText || ''} ${option.value}`
    .toLowerCase()
    .trim();
  return haystack.includes(query);
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  searchPlaceholder = 'Type to search…',
  disabled = false,
  title,
  'aria-label': ariaLabel,
  size = 'default',
  dense = false,
  fill = false,
  className = '',
  autoOpen = false,
  onClose,
  clearLabel = 'Clear',
  allowClear = true,
  variant = 'default',
  priorityValues,
}) => {
  const listboxId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(autoOpen);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [menuPos, setMenuPos] = useState<AnchoredMenuPos | null>(null);

  const selected = useMemo(
    () => options.find((option) => option.value === value) || null,
    [options, value]
  );

  const triggerLabel = selected
    ? selected.displayLabel || selected.label
    : value
      ? value
      : placeholder;

  const countryGroups = useMemo(() => {
    if (variant !== 'country') return null;
    return partitionCountrySelectOptions(options, priorityValues);
  }, [variant, options, priorityValues]);

  const filteredPriority = useMemo(() => {
    if (!countryGroups) return [] as SearchableSelectOption[];
    const normalised = query.trim().toLowerCase();
    return countryGroups.priority.filter((option) => matchesQuery(option, normalised));
  }, [countryGroups, query]);

  const filteredRest = useMemo(() => {
    const normalised = query.trim().toLowerCase();
    if (!countryGroups) {
      return options.filter((option) => matchesQuery(option, normalised));
    }
    return countryGroups.rest.filter((option) => matchesQuery(option, normalised));
  }, [countryGroups, options, query]);

  const filteredOptions = useMemo(
    () => (countryGroups ? [...filteredPriority, ...filteredRest] : filteredRest),
    [countryGroups, filteredPriority, filteredRest]
  );

  const showCountryDivider = filteredPriority.length > 0 && filteredRest.length > 0;

  const placeMenu = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const next = placeAnchoredMenu({
      trigger: rect,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      menuHeight: menuRef.current?.offsetHeight,
    });
    setMenuPos((previous) => (isSameAnchoredMenuPos(previous, next) ? previous : next));
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlightIndex(0);
    onClose?.();
  }, [onClose]);

  const selectValue = useCallback(
    (next: string) => {
      onChange(next);
      setOpen(false);
      setQuery('');
      setHighlightIndex(0);
      triggerRef.current?.focus();
    },
    [onChange]
  );

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    placeMenu();
  }, [open, placeMenu, filteredOptions.length]);

  useEffect(() => {
    if (!open) return;
    const frame = window.requestAnimationFrame(() => {
      searchRef.current?.focus();
      searchRef.current?.select();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    setHighlightIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    };

    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    document.addEventListener('mousedown', handlePointerDown);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [open, placeMenu, closeMenu]);

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeMenu();
      triggerRef.current?.focus();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, Math.max(filteredOptions.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = filteredOptions[highlightIndex];
      if (option && !option.disabled) {
        selectValue(option.value);
      }
    }
  };

  const sizeClasses = fill
    ? 'min-w-0 w-full max-w-none'
    : size === 'compact'
      ? 'min-w-[3.25rem] max-w-[4.75rem] w-auto'
      : 'min-w-[6.5rem] max-w-[9.5rem] w-full';

  const renderOption = (option: SearchableSelectOption, index: number) => {
    const isSelected = option.value === value;
    const isHighlighted = index === highlightIndex;
    return (
      <button
        key={option.value}
        type="button"
        role="option"
        aria-selected={isSelected}
        disabled={option.disabled}
        onMouseEnter={() => setHighlightIndex(index)}
        onClick={() => {
          if (option.disabled) return;
          selectValue(option.value);
        }}
        className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs disabled:cursor-not-allowed disabled:opacity-50 ${
          isSelected
            ? 'bg-primary-500/10 font-medium text-primary-800'
            : isHighlighted
              ? 'bg-primary-50 text-primary-800'
              : 'text-primary-700 hover:bg-primary-50'
        }`}
        title={option.label}
      >
        <span className="min-w-0 flex-1 truncate">{option.label}</span>
        {variant === 'country' ? (
          <>
            {option.displayLabel ? (
              <span className="shrink-0 tabular-nums text-primary-500/60">
                {option.displayLabel}
              </span>
            ) : null}
            <span className="inline-flex h-3 w-3 shrink-0 items-center justify-center" aria-hidden>
              {isSelected ? (
                <CheckIcon className="h-3 w-3 text-primary-600" />
              ) : null}
            </span>
          </>
        ) : null}
      </button>
    );
  };

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel || placeholder}
          style={{
            position: 'fixed',
            top: menuPos?.placement === 'above' ? 'auto' : menuPos?.top,
            bottom: menuPos?.placement === 'above' ? menuPos.bottom : 'auto',
            left: menuPos?.left ?? 0,
            width: menuPos?.width ?? 220,
            zIndex: 10000,
            maxHeight: menuPos?.maxHeight,
            visibility: menuPos ? 'visible' : 'hidden',
          }}
          className="flex flex-col overflow-hidden rounded-lg border border-primary-500/20 bg-white shadow-lg"
        >
            <div className="flex items-center gap-1.5 border-b border-primary-500/10 px-2 py-1.5">
              <MagnifyingGlassIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary-500/50" aria-hidden />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={searchPlaceholder}
                className="min-w-0 flex-1 bg-transparent text-xs text-primary-800 placeholder:text-primary-500/45 focus:outline-none"
                aria-autocomplete="list"
                aria-controls={listboxId}
              />
            </div>
            <div className="overflow-y-auto py-1">
              {allowClear && (
                <button
                  type="button"
                  role="option"
                  aria-selected={value === ''}
                  onClick={() => selectValue('')}
                  className={`flex w-full px-2.5 py-1.5 text-left text-xs ${
                    value === ''
                      ? 'bg-primary-500/10 font-medium text-primary-800'
                      : 'text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  {clearLabel}
                </button>
              )}
              {filteredOptions.length === 0 ? (
                <p className="px-2.5 py-2 text-xs text-primary-500/70">No matches</p>
              ) : countryGroups ? (
                <>
                  {filteredPriority.map((option, index) => renderOption(option, index))}
                  {showCountryDivider ? (
                    <div
                      role="separator"
                      className="my-1 border-t border-primary-500/15"
                    />
                  ) : null}
                  {filteredRest.map((option, index) =>
                    renderOption(option, index + filteredPriority.length)
                  )}
                </>
              ) : (
                filteredOptions.map((option, index) => renderOption(option, index))
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className={`relative inline-flex ${sizeClasses} ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        title={title || (selected ? selected.label : value || placeholder)}
        aria-label={ariaLabel || placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        className={`inline-flex w-full items-center gap-0.5 rounded border border-primary-500/20 bg-white pl-1.5 pr-1 text-left text-xs text-primary-800 transition-none focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/20 disabled:cursor-not-allowed disabled:opacity-50 ${
          fill ? 'h-8 min-h-[32px]' : dense ? 'h-6 min-h-[24px]' : 'h-7 min-h-[28px]'
        } ${
          open ? 'border-primary-500 ring-1 ring-primary-500/20' : 'hover:border-primary-500/40'
        }`}
      >
        <span className={`min-w-0 flex-1 truncate ${selected ? 'text-primary-800' : 'text-primary-500/55'}`}>
          {triggerLabel}
        </span>
        <ChevronDownIcon
          className={`h-3.5 w-3.5 flex-shrink-0 text-primary-500/55 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {menu}
    </div>
  );
};

export default SearchableSelect;
